import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Loader2, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Qual o horário de funcionamento?",
  "Vocês fazem entrega?",
  "Tem ração para gato filhote?",
];

const GREETING =
  "Oi! Sou o assistente virtual da Agro Carmo. Posso ajudar com produtos, preços, horários e serviços. Em que posso ajudar?";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(`chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const current = acc;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: current };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Tive um probleminha técnico. Chame a gente no WhatsApp (61) 99965-5654!",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-open-button"
        initial={{ opacity: 0, scale: 0.6, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 260, damping: 20 }}
        className="fixed bottom-6 left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-olive text-parchment shadow-2xl shadow-olive/40 transition-transform duration-300 hover:scale-110"
        aria-label={open ? "Fechar assistente" : "Abrir assistente virtual"}
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chat-panel"
            className="fixed bottom-24 left-4 z-50 flex h-[30rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-[1.5rem] border border-linen bg-parchment shadow-2xl sm:left-6"
          >
            <div className="flex items-center gap-3 bg-forest px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-olive">
                <Bot className="h-5 w-5 text-ochre" />
              </span>
              <div>
                <p className="font-display text-base font-semibold text-parchment">
                  Assistente Agro Carmo
                </p>
                <p className="flex items-center gap-1.5 text-xs text-parchment/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#25D366]" />
                  Online 24h
                </p>
              </div>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-olive text-parchment"
                        : "rounded-bl-sm border border-linen bg-white text-ink"
                    }`}
                  >
                    {m.text || <Loader2 className="h-4 w-4 animate-spin text-moss" />}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${i}`}
                    className="rounded-full border border-linen bg-white px-3 py-1.5 text-xs text-olive transition-colors hover:bg-linen/50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-linen bg-white px-4 py-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escreva sua dúvida..."
                data-testid="chat-input"
                className="flex-1 rounded-full border border-linen bg-parchment px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-moss/60 focus:border-clay"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-clay text-parchment transition-colors hover:bg-olive disabled:opacity-50"
                aria-label="Enviar mensagem"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
